import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { resendVerification, verifyEmail } from "../api/apiClient";
import { useTenant } from "../contexts/TenantContext";
import AuthLayout, { AuthAlert } from "./auth/AuthLayout";
import AuthField from "./auth/AuthField";

/** Landing page for the emailed "confirm your email" link: /verify-email?token=... */
export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { domainTenant } = useTenant();
  const token = params.get("token");
  // The token is single-use; React StrictMode runs effects twice in development.
  const started = useRef(false);

  const [status, setStatus] = useState(token ? "verifying" : "failed");
  const [error, setError] = useState(token ? null : "This confirmation link is missing its token.");
  const [email, setEmail] = useState("");
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    verifyEmail(token)
      .then(() => setStatus("verified"))
      .catch((err) => {
        setError(err?.response?.data?.message || "This link is invalid or has expired.");
        setStatus("failed");
      });
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setResending(true);
    try {
      await resendVerification(email.trim());
    } catch {
      // The endpoint never reveals whether the address exists; treat any failure the same way.
    } finally {
      setResending(false);
      setResent(true);
    }
  };

  return (
    <AuthLayout domainTenant={domainTenant}>
      {status === "verifying" && (
        <>
          <h1>Confirming your email</h1>
          <p className="auth-sub">One moment…</p>
          <CircularProgress size={24} aria-label="Confirming your email" />
        </>
      )}

      {status === "verified" && (
        <>
          <div className="auth-ico">
            <CheckIcon />
          </div>
          <h1>Email confirmed</h1>
          <p className="auth-sub">Your account is active. You can sign in now.</p>
          <button
            type="button"
            className="auth-btn auth-btn--p"
            style={{ width: "100%" }}
            onClick={() => navigate("/login")}
          >
            Continue to sign in
          </button>
        </>
      )}

      {status === "failed" && (
        <>
          <h1>Link not valid</h1>
          <p className="auth-sub">Request a new confirmation link and we&apos;ll email it to you.</p>
          {error && <AuthAlert>{error}</AuthAlert>}
          {resent ? (
            <div className="auth-ico">
              <MailOutlineIcon />
            </div>
          ) : null}
          {resent ? (
            <p className="auth-sub">
              If <span className="auth-mail">{email.trim()}</span> needs confirming, a new link is on its way.
            </p>
          ) : (
            <form className="auth-form" onSubmit={handleResend} noValidate>
              <AuthField
                id="resend-email"
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
              />
              <button className="auth-btn auth-btn--p" type="submit" disabled={resending}>
                Send a new link
              </button>
            </form>
          )}
          <div className="auth-alt">
            <button type="button" className="auth-link" onClick={() => navigate("/login")}>
              Back to sign in
            </button>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
